import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { BLACK, FEED_BACK, RED } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import { SAVE_STATUS } from '../constants';

const statusIconSx = { fontSize: '14px' };

const spinnerSx = { color: BLACK[70] };

const rootSx = { alignItems: 'center', display: 'inline-flex', gap: '4px' };

const textSx = { fontFamily: PRIMARY_FONT[400], fontSize: '12px' };

const STATUS_CONFIG = {
  [SAVE_STATUS.SAVING]: {
    color: BLACK[70],
    icon: <CircularProgress size={12} sx={spinnerSx} />,
    messageId: 'pt.detail.saveStatus.saving',
  },
  [SAVE_STATUS.SAVED]: {
    color: FEED_BACK.GREEN_80,
    icon: <CheckIcon sx={statusIconSx} />,
    messageId: 'pt.detail.saveStatus.saved',
  },
  [SAVE_STATUS.ERROR]: {
    color: RED[80],
    icon: <ErrorOutlineIcon sx={statusIconSx} />,
    messageId: 'pt.detail.saveStatus.error',
  },
  [SAVE_STATUS.LOCAL_ONLY]: {
    color: BLACK[70],
    icon: <InfoOutlinedIcon sx={statusIconSx} />,
    messageId: 'pt.detail.saveStatus.localOnly',
  },
};

const STATUS_SX = Object.fromEntries(
  Object.entries(STATUS_CONFIG).map(([status, { color }]) => [
    status,
    { root: { ...rootSx, color }, text: { ...textSx, color } },
  ])
);

const SaveStatusIndicator = ({ status }) => {
  const intl = useIntl();
  const config = STATUS_CONFIG[status];
  if (!config) return null;
  const sx = STATUS_SX[status];

  return (
    <Box data-testid={`save-status-${status}`} role="status" sx={sx.root}>
      {config.icon}
      <Typography sx={sx.text}>{intl.formatMessage({ id: config.messageId })}</Typography>
    </Box>
  );
};

SaveStatusIndicator.propTypes = {
  status: PropTypes.oneOf(Object.values(SAVE_STATUS)),
};

export default SaveStatusIndicator;
